// Clean unit: the URL contract of the wallet modal, with NO runtime imports.
// `?modal=wallet&tab=deposit&method=crypto` arrives as raw strings from the
// address bar, so nothing below may assume the pair is valid.

export const WALLET_TABS = ["deposit", "withdrawal"] as const
export const DEPOSIT_METHODS = ["apm", "card", "crypto", "cash"] as const
export const WITHDRAWAL_METHODS = ["crypto", "bank"] as const

export type WalletTab = (typeof WALLET_TABS)[number]
export type DepositMethod = (typeof DEPOSIT_METHODS)[number]
export type WithdrawalMethod = (typeof WITHDRAWAL_METHODS)[number]

// Discriminated, not `{ tab, method }`: `card` is a deposit method only, and
// the union is what stops a withdrawal branch from rendering a card form.
export type WalletRoute =
  | { tab: "deposit"; method: DepositMethod }
  | { tab: "withdrawal"; method: WithdrawalMethod }

export type WalletRouteParams = { tab?: string; method?: string }

// Spanish leftovers from the visible labels. Read them, never write them —
// `canonicalWalletQuery` always returns the English ids.
const TAB_ALIASES: Record<string, WalletTab> = {
  deposito: "deposit",
  retirar: "withdrawal"
}

const METHOD_ALIASES: Record<string, DepositMethod | WithdrawalMethod> = {
  efectivo: "cash",
  tarjetas: "card",
  cripto: "crypto",
  bancos: "bank"
}

function resolveTab(raw: string | undefined): WalletTab {
  const aliased = raw === undefined ? undefined : (TAB_ALIASES[raw] ?? raw)
  return WALLET_TABS.find((tab) => tab === aliased) ?? WALLET_TABS[0]
}

function resolveMethod<M extends string>(
  allowed: readonly M[],
  raw: string | undefined
): M {
  return allowed.find((method) => method === raw) ?? allowed[0]
}

function resolveAliasedMethod(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined
  return METHOD_ALIASES[raw] ?? raw
}

// The pair is validated as a whole: `tab=withdrawal&method=card` is not a
// withdrawal on a broken method, it is a withdrawal on its first one.
export function parseWalletRoute(raw: WalletRouteParams): WalletRoute {
  const tab = resolveTab(raw.tab)
  const method = resolveAliasedMethod(raw.method)

  if (tab === "withdrawal") {
    return {
      tab: "withdrawal",
      method: resolveMethod(WITHDRAWAL_METHODS, method)
    }
  }

  return { tab: "deposit", method: resolveMethod(DEPOSIT_METHODS, method) }
}

// Owned query keys in English, including defaults. `?modal=wallet` becomes
// `tab=deposit&method=apm`; `method=efectivo` becomes `method=cash`.
export function canonicalWalletQuery(
  raw: Record<string, string>
): Record<string, string> {
  const route = parseWalletRoute({ tab: raw.tab, method: raw.method })
  return { tab: route.tab, method: route.method }
}
